import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./discoverMapPage.css";
import generalRequest from "../../request/generalRequest";
import MapEvents from "../../utils/mapEvents";
import { Link, useNavigate } from "react-router-dom";
import DiscoverMapListSkeleton from "./discoverMapPageSkeleton/discoverMapPageSkeleton";
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import { Search, SlidersHorizontal, X } from "lucide-react";
import MapFilters, { DEFAULT_MAP_FILTERS, buildFacets, countActiveFilters, filterShops } from "./mapFilters/mapFilters";

const formatRating = (rating) => {
	if (rating == null) return null;

	return Number(rating || 0).toFixed(1);
};

/* ---------------- HELPERS ---------------- */

const DEFAULT_CENTER = { lat: 18.4861, lng: -69.9312, zoom: 13 };
const MOVE_THRESHOLD_KM = 8; // city-level movement
const MAPBOX_TOKEN = process.env.REACT_APP_MAP_BOX_TOKEN;

const kmBetween = (a, b) => {
	const R = 6371;
	const dLat = ((b.lat - a.lat) * Math.PI) / 180;
	const dLng = ((b.lng - a.lng) * Math.PI) / 180;

	const lat1 = (a.lat * Math.PI) / 180;
	const lat2 = (b.lat * Math.PI) / 180;

	const x = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);

	return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};

const hasCoords = (shop) => typeof shop?.lat === "number" && typeof shop?.lng === "number";

/* Leaflet reports a 0×0 size until the layout settles, and getBounds() then
   collapses to a point — a viewport that matches nothing. */
const isUsableBounds = (b) => Boolean(b) && b.ne.lat !== b.sw.lat && b.ne.lng !== b.sw.lng;

/* ---------------- MAP PINS ---------------- */

/* Module-level so the cache actually survives re-renders. */
const markerIconCache = new Map();

const getMarkerIcon = (rating, active) => {
	const displayRating = rating ? formatRating(rating) : "";
	const rated = Boolean(displayRating);
	const cacheKey = `${rated ? displayRating : "plain"}|${active ? "on" : "off"}`;

	if (markerIconCache.has(cacheKey)) return markerIconCache.get(cacheKey);

	const icon = L.divIcon({
		className: "sf-pin-wrap",
		html: `
			<div class="sf-pin ${rated ? "" : "sf-pin--plain"} ${active ? "is-active" : ""}">
				${rated ? `<span class="sf-pin-body"><span class="sf-pin-star">★</span>${displayRating}</span>` : `<span class="sf-pin-body"></span>`}
			</div>
		`,
		iconSize: rated ? [58, 42] : [26, 34],
		iconAnchor: rated ? [29, 42] : [13, 34],
		popupAnchor: [0, rated ? -40 : -32],
	});

	markerIconCache.set(cacheKey, icon);
	return icon;
};

/* Gives the page a handle on the Leaflet instance (react-leaflet v4 has no whenCreated). */
const MapBridge = ({ mapRef }) => {
	const map = useMap();

	useEffect(() => {
		mapRef.current = map;

		// The map is created inside a flex layout, so its first measure can be stale.
		// Measure now *and* on the next frame — rAF alone never fires on a hidden tab.
		map.invalidateSize();
		const frame = requestAnimationFrame(() => map.invalidateSize());

		return () => {
			cancelAnimationFrame(frame);
			mapRef.current = null;
		};
	}, [map, mapRef]);

	return null;
};

const MapZoomControls = ({ labels }) => {
	const map = useMap();

	return (
		<div className="sf-map-zoom">
			<button type="button" aria-label={labels.zoomIn} onClick={() => map.zoomIn()}>
				<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
					<path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
				</svg>
			</button>

			<span className="sf-map-zoom-sep" aria-hidden="true" />

			<button type="button" aria-label={labels.zoomOut} onClick={() => map.zoomOut()}>
				<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
					<path d="M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
				</svg>
			</button>
		</div>
	);
};

const PinIcon = () => (
	<svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true" className="sf-map-pin-glyph">
		<path
			d="M12 21s7-6.1 7-11a7 7 0 1 0-14 0c0 4.9 7 11 7 11z"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinejoin="round"
		/>
		<circle cx="12" cy="10" r="2.4" fill="currentColor" />
	</svg>
);

const ShopMapPopup = ({ shop, copy }) => {
	const rating = formatRating(shop.rating);

	return (
		<div className="sf-popup-card">
			<Link to={`/shops/${shop.slug || shop.shopId}`}>
				<div className="sf-popup-media">
					{shop.coverImage ? <img src={shop.coverImage} alt={shop.name} /> : <div className="sf-popup-image-placeholder" />}

					<span className="sf-popup-scrim" aria-hidden="true" />

					{shop.rating ? (
						<span className="sf-popup-rating">
							<span className="sf-popup-star">★</span>
							{rating}
							{shop.reviewCount ? <i>({shop.reviewCount})</i> : null}
						</span>
					) : null}
				</div>

				<div className="sf-popup-body">
					<h4 className="sf-popup-title">{shop.name}</h4>

					{shop.city && (
						<p className="sf-popup-address">
							<PinIcon />
							<span>
								{shop.city}
								{shop.country ? `, ${shop.country}` : ""}
							</span>
						</p>
					)}

					<span className="sf-popup-cta">
						{copy.viewShop}
						<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
							<path d="M5 12h13M12 5l7 7-7 7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
						</svg>
					</span>
				</div>
			</Link>
		</div>
	);
};

const MapServicesList = ({ shop, moreLabel }) => {
	/* With a service filter on, the card shows what actually matched instead of
	   the shop's first three — otherwise the row you searched for stays hidden. */
	const services = shop.matchedServices || shop.services;

	if (!Array.isArray(services) || services.length === 0) return null;

	const visibleServices = services.slice(0, 3);
	const hiddenCount = services.length - visibleServices.length;

	return (
		<div className="sf-map-services">
			{visibleServices.map((service) => (
				<div key={service.serviceId} className="sf-map-service-row">
					<span className="sf-map-service-name" title={service.name}>
						{service.name}
					</span>

					<span className="sf-map-service-meta">
						{service.duration ? <span className="sf-map-service-duration">{service.duration} min</span> : null}

						<span className="sf-map-service-price">
							{service.price} {service.currency}
						</span>
					</span>
				</div>
			))}

			{hiddenCount > 0 && <div className="sf-map-services-more">{moreLabel(hiddenCount)}</div>}
		</div>
	);
};

/* ---------------- COMPONENT ---------------- */

const DiscoverMapPage = () => {
	const [shops, setShops] = useState([]);
	/* The last read that wasn't narrowed server-side — the chips' source */
	const [facetShops, setFacetShops] = useState([]);
	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [activeShopId, setActiveShopId] = useState(null);
	const [lastFetchCenter, setLastFetchCenter] = useState(null);
	const [navOffset, setNavOffset] = useState(0);

	const [filters, setFilters] = useState(DEFAULT_MAP_FILTERS);
	const [filtersOpen, setFiltersOpen] = useState(false);

	const mapRef = useRef(null);
	const cardRefs = useRef({});
	/* Area the chip counts describe — null until the map narrows one */
	const facetScopeRef = useRef(null);
	/* Bumped per read so a slow response can't overwrite a newer one */
	const requestSeqRef = useRef(0);
	/* Set while we move the map ourselves, so the resulting moveend doesn't
	   clear the selection the move was made to reveal. */
	const programmaticMoveRef = useRef(false);

	const navigate = useNavigate();
	const { language } = useContext(CombinedContext);

	const t = translations[language].discover;
	const copy = t.map;
	const filterCopy = copy.filters;

	/* Category and specialty are the coarse cuts — they travel to the server so
	   it can answer from a GSI instead of scanning the table. Everything else
	   stays local and instant. The local pass still applies every filter, so
	   the page is correct whether or not the server honours the params. */
	const coarseParams = useMemo(
		() => ({
			...(filters.category !== "all" ? { category: filters.category } : {}),
			...(filters.subCategory !== "all" ? { subCategory: filters.subCategory } : {}),
		}),
		[filters.category, filters.subCategory]
	);

	/* Once the server narrows, `shops` is only that slice — so the chips build
	   from the last unnarrowed read instead, or they'd collapse to the single
	   category you just picked and strand you there. */
	const facets = useMemo(() => buildFacets(facetShops, language), [facetShops, language]);
	const visibleShops = useMemo(() => filterShops(shops, filters), [shops, filters]);
	const activeFilterCount = countActiveFilters(filters);

	const updateFilters = useCallback((patch) => setFilters((prev) => ({ ...prev, ...patch })), []);
	const resetFilters = useCallback(() => setFilters(DEFAULT_MAP_FILTERS), []);

	/* Initial load — stable endpoint. Routed through fetchShops so it shares the
	   sequence guard: the map's first moveend fires its own read moments later,
	   and without that this one could land last and clobber the fresher data. */
	useEffect(() => {
		const load = async () => {
			setLoading(true);
			const res = await fetchShops(null);
			if (!res.stale) setShops(res.shops || []);
			setLoading(false);
		};
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/* A filter can hide the shop that's currently pinned — drop the selection
	   rather than leave a highlighted marker with no card behind it. */
	useEffect(() => {
		if (!activeShopId) return;
		if (!visibleShops.some((shop) => shop.shopId === activeShopId)) setActiveShopId(null);
	}, [visibleShops, activeShopId]);

	/* The header sits in normal flow and changes height by breakpoint —
	   measure it so the map fills exactly the rest of the viewport. */
	useEffect(() => {
		const nav = document.querySelector(".sf-nav");
		if (!nav) return;

		const measure = () => setNavOffset(nav.getBoundingClientRect().height);
		measure();

		if (typeof ResizeObserver === "undefined") {
			window.addEventListener("resize", measure);
			return () => window.removeEventListener("resize", measure);
		}

		const observer = new ResizeObserver(measure);
		observer.observe(nav);
		return () => observer.disconnect();
	}, []);

	/* Keep Leaflet in sync when the container is resized by the layout. */
	useEffect(() => {
		if (!mapRef.current) return;

		mapRef.current.invalidateSize();
		const frame = requestAnimationFrame(() => mapRef.current?.invalidateSize());

		return () => cancelAnimationFrame(frame);
	}, [navOffset, loading]);

	/* Memoised so the coarse-filter effect below has a stable dependency —
	   a fresh function each render would refetch on every render. */
	const fetchShops = useCallback(
		async (bounds) => {
			/* Mount and the map's first moveend both fire a read, and they can
			   land out of order — so a response only counts if it is still the
			   newest one asked for. `stale` lets callers drop the rest. */
			const seq = (requestSeqRef.current += 1);

			setRefreshing(true);

			try {
				const res = await generalRequest.getPublicShopsForMap({
					...(bounds
						? { neLat: bounds.ne.lat, neLng: bounds.ne.lng, swLat: bounds.sw.lat, swLng: bounds.sw.lng }
						: {}),
					...coarseParams,
				});

				if (seq !== requestSeqRef.current) return { ...res, stale: true };

				/* Only an unnarrowed read describes the whole area, so only that
				   one refreshes the chips — and we remember the area it covered
				   so a narrowed read can ask about exactly the same ground. */
				if (!coarseParams.category && !coarseParams.subCategory && (!bounds || isUsableBounds(bounds))) {
					facetScopeRef.current = bounds || null;
					setFacetShops(res.shops || []);
				}

				return { ...res, stale: false };
			} finally {
				setRefreshing(false);
			}
		},
		[coarseParams]
	);

	/* The server answers category and specialty, so changing either needs a
	   fresh read of the current viewport. Search, rating and service type are
	   resolved locally and never reach here. */
	const coarseKey = `${filters.category}|${filters.subCategory}`;
	const lastCoarseKey = useRef(coarseKey);

	useEffect(() => {
		/* Skips the mount pass — the initial load already covered it */
		if (lastCoarseKey.current === coarseKey) return undefined;
		lastCoarseKey.current = coarseKey;

		let cancelled = false;

		(async () => {
			/* Reuse the area the chips were counted over. Asking a narrower one
			   would let a chip promise "1" and then return nothing — the counts
			   come from the last unnarrowed read, so this must match it. */
			const res = await fetchShops(facetScopeRef.current);
			if (!cancelled && !res.stale) setShops(res.shops || []);
		})();

		return () => {
			cancelled = true;
		};
	}, [coarseKey, fetchShops]);

	/* Map movement hook — refetch ONLY on large moves */
	const handleBoundsChange = async ({ ne, sw, center }) => {
		// First movement → fetch once
		if (!lastFetchCenter) {
			setLastFetchCenter(center);

			const res = await fetchShops({ ne, sw });

			if (!res.stale && res.shops?.length) {
				setShops(res.shops);
			}
			return;
		}

		const movedKm = kmBetween(lastFetchCenter, center);

		// Ignore zoom / tiny pans
		if (movedKm < MOVE_THRESHOLD_KM) return;

		// Significant move → refetch
		setLastFetchCenter(center);

		if (programmaticMoveRef.current) {
			programmaticMoveRef.current = false;
		} else {
			setActiveShopId(null);
		}

		const res = await fetchShops({ ne, sw });
		if (!res.stale) setShops(res.shops || []);
	};

	/* List → map */
	const focusShopOnMap = (shop) => {
		const map = mapRef.current;
		if (!map || !hasCoords(shop)) return;

		setActiveShopId(shop.shopId);
		programmaticMoveRef.current = true;

		// Refresh the size cache first: flyTo divides by it and yields NaN when it is 0.
		map.invalidateSize();

		const currentZoom = map.getZoom();
		const targetZoom = Math.max(Number.isFinite(currentZoom) ? currentZoom : DEFAULT_CENTER.zoom, 15);
		const size = map.getSize();

		if (!size.x || !size.y) {
			map.setView([shop.lat, shop.lng], targetZoom);
			return;
		}

		map.flyTo([shop.lat, shop.lng], targetZoom, { duration: 0.7 });
	};

	/* Map → list */
	const handleMarkerSelect = (shopId) => {
		setActiveShopId(shopId);
		cardRefs.current[shopId]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
	};

	const openShop = (shop) => navigate(`/shops/${shop.slug || shop.shopId}`);

	return (
		<div className="sf-map-page" style={{ "--sfm-offset": `${navOffset}px` }}>
			{/* LEFT / SIDE LIST */}
			<aside className="sf-map-panel">
				<header className="sf-map-panel-head">
					{/* <span className="sf-map-eyebrow">{copy.eyebrow}</span> */}

					<div className="sf-map-head-row">
						<h1 className="sf-map-title">
							{t.title}
							<span className="sf-map-count">{visibleShops.length}</span>
						</h1>

						<Link className="sf-map-viewtoggle" to="/barbershop/barbershop">
							<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
								<path d="M4 6h16M4 12h16M4 18h16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
							</svg>
							{copy.listView}
						</Link>
					</div>

					<p className="sf-map-subtitle">{copy.subtitle}</p>

					{/* Search stays on the surface — it's the filter people reach
					    for first; the rest live one click away in the sheet. */}
					<div className="sf-map-filterbar">
						<div className="sf-map-search">
							<Search size={15} strokeWidth={2.2} className="sf-map-search-glyph" />

							<input
								type="search"
								value={filters.query}
								placeholder={filterCopy.searchPlaceholder}
								aria-label={filterCopy.searchLabel}
								onChange={(e) => updateFilters({ query: e.target.value })}
							/>

							{filters.query && (
								<button
									type="button"
									className="sf-map-search-clear"
									aria-label={filterCopy.clearSearch}
									onClick={() => updateFilters({ query: "" })}
								>
									<X size={14} strokeWidth={2.4} />
								</button>
							)}
						</div>

						<button
							type="button"
							className={`sf-map-filterbtn${activeFilterCount > 0 ? " is-active" : ""}`}
							aria-label={filterCopy.open}
							aria-expanded={filtersOpen}
							onClick={() => setFiltersOpen(true)}
						>
							<SlidersHorizontal size={15} strokeWidth={2.2} />
							<span className="sf-map-filterbtn-label">{filterCopy.open}</span>
							{activeFilterCount > 0 && <span className="sf-map-filterbtn-badge">{activeFilterCount}</span>}
						</button>
					</div>
				</header>

				<div className="sf-map-cards">
					{loading ? (
						<DiscoverMapListSkeleton count={6} />
					) : visibleShops.length === 0 ? (
						<div className="sf-map-empty">
							<span className="sf-map-empty-glyph" aria-hidden="true">
								<PinIcon />
							</span>

							{/* Nothing nearby and nothing matching are different
							    problems, and they need different next steps. */}
							<p className="sf-map-empty-title">{activeFilterCount > 0 ? filterCopy.emptyTitle : t.empty}</p>
							<p className="sf-map-empty-hint">{activeFilterCount > 0 ? filterCopy.emptyHint : copy.emptyHint}</p>

							{activeFilterCount > 0 && (
								<button type="button" className="sf-map-empty-btn" onClick={resetFilters}>
									{filterCopy.reset}
								</button>
							)}
						</div>
					) : (
						visibleShops.map((shop) => (
							<article
								key={shop.shopId}
								ref={(el) => {
									cardRefs.current[shop.shopId] = el;
								}}
								className={`sf-map-card ${activeShopId === shop.shopId ? "is-active" : ""}`}
								onClick={() => openShop(shop)}
								onMouseEnter={() => setActiveShopId(shop.shopId)}
								onMouseLeave={() => setActiveShopId((current) => (current === shop.shopId ? null : current))}
							>
								<div className="sf-map-card-media">
									{shop.coverImage ? <img src={shop.coverImage} alt={shop.name} /> : <div className="sf-map-card-placeholder" />}

									<span className="sf-map-card-scrim" aria-hidden="true" />

									{shop.rating ? (
										<span className="sf-map-card-rating">
											<span className="sf-map-card-star">★</span>
											{formatRating(shop.rating)}
											{shop.reviewCount ? <i>({shop.reviewCount})</i> : null}
										</span>
									) : null}

									{hasCoords(shop) && (
										<button
											type="button"
											className="sf-map-card-locate"
											aria-label={copy.showOnMap}
											title={copy.showOnMap}
											onClick={(e) => {
												e.stopPropagation();
												focusShopOnMap(shop);
											}}
										>
											<PinIcon />
										</button>
									)}
								</div>

								<div className="sf-map-card-body">
									<Link
										className="sf-map-card-name"
										to={`/shops/${shop.slug || shop.shopId}`}
										onClick={(e) => e.stopPropagation()}
									>
										{shop.name}
									</Link>

									{shop.address && (
										<p className="sf-map-card-address">
											<PinIcon />
											<span>{shop.address}</span>
										</p>
									)}

									<MapServicesList shop={shop} moreLabel={t.services.more} />

									<span className="sf-map-card-cta">
										{copy.viewShop}
										<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
											<path
												d="M5 12h13M12 5l7 7-7 7"
												fill="none"
												stroke="currentColor"
												strokeWidth="2.2"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</span>
								</div>
							</article>
						))
					)}
				</div>
			</aside>

			{/* MAP */}
			<div className="sf-map-canvas">
				<MapContainer
					center={[DEFAULT_CENTER.lat, DEFAULT_CENTER.lng]}
					zoom={DEFAULT_CENTER.zoom}
					className="sf-map"
					scrollWheelZoom
					attributionControl={false}
					zoomControl={false}
				>
					{MAPBOX_TOKEN ? (
						<TileLayer
							url={`https://api.mapbox.com/styles/v1/iwhite/cmjwicu52001001qo18qxh9i4/tiles/512/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
							tileSize={512}
							zoomOffset={-1}
							attribution="© Mapbox © OpenStreetMap"
						/>
					) : (
						<TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap contributors" />
					)}

					<MapBridge mapRef={mapRef} />
					<MapEvents onBoundsChange={handleBoundsChange} />
					<MapZoomControls labels={copy} />

					{visibleShops.map((shop) =>
						hasCoords(shop) ? (
							<Marker
								key={shop.shopId}
								position={[shop.lat, shop.lng]}
								icon={getMarkerIcon(shop.rating, activeShopId === shop.shopId)}
								zIndexOffset={activeShopId === shop.shopId ? 1000 : 0}
								eventHandlers={{ click: () => handleMarkerSelect(shop.shopId) }}
							>
								<Popup
									closeButton
									autoPan
									autoPanPadding={[28, 28]}
									offset={[0, -10]}
									className="sf-map-popup"
									autoClose={true}
									closeOnClick={false}
								>
									<ShopMapPopup shop={shop} copy={copy} />
								</Popup>
							</Marker>
						) : null
					)}
				</MapContainer>

				<div className={`sf-map-toast ${refreshing ? "is-visible" : ""}`} aria-live="polite">
					<span className="sf-map-toast-spinner" aria-hidden="true" />
					{copy.updating}
				</div>

				{!loading && visibleShops.length > 0 && (
					<div className="sf-map-results-chip">{copy.results(visibleShops.length)}</div>
				)}
			</div>

			<MapFilters
				open={filtersOpen}
				onClose={() => setFiltersOpen(false)}
				filters={filters}
				onChange={updateFilters}
				onReset={resetFilters}
				facets={facets}
				resultCount={visibleShops.length}
				copy={filterCopy}
			/>
		</div>
	);
};

export default DiscoverMapPage;
